import { useCallback, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { useSnackbar } from "notistack";

import { useIoCContext } from "@context/IoCContext/IoCContext";
import { Types } from "@ioc/types";
import { IGetPriceProductsService } from "@modules/orders/models/IGetPriceProductsService";
import { IProduct, Price } from "@utils/interfaces";

import { useAppointmentPriceContext } from "../context/AppointmentPriceContext";
import { IQuery } from "../interface";
import { fetchProductPrice } from "../services/fetchProductPrice";
import {
  calculateDisplayPrice,
  getInitialDeadlinePayment,
} from "../rules/price.rules";

export function useProductPrice(product: IProduct) {
  const { dataCustomer, customerStatus } = useAppointmentPriceContext();
  const { values } = useFormikContext<IQuery>();
  const { enqueueSnackbar } = useSnackbar();
  const iocContext = useIoCContext();

  const service = iocContext.serviceContainer.get<IGetPriceProductsService>(
    Types.Orders.IGetPriceProductsService
  );

  const [loadingPrice, setLoadingPrice] = useState(true);
  const [deadlinePayment, setDeadlinePayment] = useState(
    getInitialDeadlinePayment(product.payCond, dataCustomer?.payCond)
  );
  const [price, setPrice] = useState<Price | null>(null);

  const fetchPrice = useCallback(async () => {
    if (!dataCustomer || !values.address || !values.filialID) return;

    setLoadingPrice(true);

    const { data, error } = await fetchProductPrice(service, {
      addressID: values.address.id,
      customerID: dataCustomer.id,
      deadlinePayment,
      filialID: values.filialID,
      productID: product.id,
      transportationZone: values.address.transportationZone,
    });

    if (error) {
      enqueueSnackbar(error.message, { variant: error.variant });
    } else {
      setPrice(data);
    }

    setLoadingPrice(false);
  }, [
    dataCustomer,
    deadlinePayment,
    enqueueSnackbar,
    service,
    product.id,
    values.address,
    values.filialID,
  ]);

  useEffect(() => {
    fetchPrice();
  }, [fetchPrice]);

  return {
    loadingPrice,
    loadingCustomerData: customerStatus === "loading",
    price,
    displayPrice: calculateDisplayPrice(price, values.freightType),
    deadlinePayment,
    setDeadlinePayment,
  };
}
